/**
 * QMS Ticket Card - Draggable ticket card component
 * Used in the Kanban board columns
 */

import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format } from 'date-fns';
import { Clock, AlertCircle, Lightbulb, CheckSquare, Building2, DollarSign } from 'lucide-react';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import type { QmsTicket } from '../../types/qms.types';
import { isPopulatedUser } from '../../types/qms.types';
import {
  QmsWorkflowBadge,
  QmsSeverityBadge,
  QmsCapaBadge,
  QmsDepartmentBadge,
  getPriorityClass,
} from '../shared/QmsBadges';

interface QmsTicketCardProps {
  ticket: QmsTicket;
  onEdit: (ticket: QmsTicket) => void;
}

export function QmsTicketCard({ ticket, onEdit }: QmsTicketCardProps) {
  const { t } = useTranslation('qms');
  const isClient = ticket.type === 'client';
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ticket._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Assignee display name for avatar alt / fallback initials
  const assigneeName = isPopulatedUser(ticket.assigned_to)
    ? `${ticket.assigned_to.firstname ?? ''} ${ticket.assigned_to.lastname ?? ''}`.trim() || ticket.assigned_to.email
    : null;
  const assigneeMongoId = isPopulatedUser(ticket.assigned_to) ? ticket.assigned_to._id : null;

  // Check if overdue
  const isOverdue = () => {
    if (!ticket.due_date) return false;
    const dueDate = new Date(ticket.due_date);
    dueDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today && ticket.status !== 'validated_closed';
  };

  // NOTE: the FDA workflow-stage SLA clock is intentionally NOT shown on the
  // card (redmark #881): it is unrelated to the ticket's due date and was
  // read as a confusing second countdown. Full SLA detail lives in the ticket
  // detail view's SLA panel (QmsSlaPanel).

  const ticketPrefix = ticket.type === 'capa' ? 'CAPA' : ticket.type === 'client' ? 'CLT' : 'QUAL';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onEdit(ticket)}
      className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer p-3 ${isClient ? 'border-l-4 border-l-emerald-500' : ''} ${getPriorityClass(ticket.priority)}`}
    >
      {/* Header with icons and badges */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Subtype icon */}
          {ticket.subtype === 'problem' && (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
          {ticket.subtype === 'improvement' && (
            <Lightbulb className="h-4 w-4 text-amber-500" />
          )}

          {/* Department badge */}
          {ticket.department && (
            <QmsDepartmentBadge department={ticket.department} />
          )}

          {/* CAPA badge */}
          {!isClient && <QmsCapaBadge type={ticket.type} needsCapa={ticket.needs_capa} />}

          {/* Severity badge */}
          {!isClient && <QmsSeverityBadge level={ticket.severity_level} />}

          {/* Client badge */}
          {isClient && ticket.client_name && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
              <Building2 className="h-3 w-3" />
              {ticket.client_name}
            </span>
          )}
        </div>

        {/* Workflow badge */}
        {!isClient && <QmsWorkflowBadge ticket={ticket} />}
      </div>

      {/* Title */}
      <h3 className="font-medium text-sm text-gray-900 mb-2 line-clamp-2">
        {ticket.title}
      </h3>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-3">
          {/* Due date */}
          {ticket.due_date && (
            <div
              className={`flex items-center gap-1 ${
                isOverdue() ? 'text-red-600 font-medium' : ''
              }`}
            >
              <Clock className="h-3 w-3" />
              {format(new Date(ticket.due_date), 'MMM d')}
            </div>
          )}

          {/* Ticket number */}
          {ticket.ticket_number && (
            <div className="flex items-center gap-1 text-gray-600">
              <CheckSquare className="h-3 w-3" />
              <span className="font-medium">
                {ticketPrefix}-{ticket.ticket_number}
              </span>
            </div>
          )}

          {/* Client ticket value */}
          {isClient && ticket.value != null && (
            <div className="flex items-center gap-1 text-emerald-600">
              <DollarSign className="h-3 w-3" />
              <span className="font-medium">{ticket.value.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Assignee avatar — photo from the shared employees cache, with initials fallback */}
        <OwnerAvatar
          mongoUserId={assigneeMongoId}
          name={assigneeName ?? t('common.unassigned', 'Unassigned')}
          className="h-6 w-6"
          fallbackClassName="bg-atek-blue text-white text-[10px] font-medium"
        />
      </div>

      {/* Tags */}
      {ticket.tags && ticket.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {ticket.tags.slice(0, 2).map((tag, idx) => (
            <span
              key={idx}
              className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600"
            >
              {tag}
            </span>
          ))}
          {ticket.tags.length > 2 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">
              +{ticket.tags.length - 2}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
